import { useEffect, useRef } from 'react';
import type { AnnotationAnchors } from '@alrimal/engine';
import type { Annotation } from '@alrimal/contracts';
import { useLocale } from '../LocaleContext.tsx';

/**
 * Room labels, in the DOM (§1).
 *
 * React owns the elements; the engine owns their positions. The anchor stream
 * writes `transform` straight onto the refs instead of going through state —
 * a camera orbit would otherwise re-render this subtree sixty times a second
 * to move some text four pixels.
 *
 * Coarse collision avoidance happens in the engine, which knows where things
 * are. The fine pass belongs here, because only the DOM knows how wide
 * «موقفين خاصين داخلية» actually is at this font size.
 */

export interface AnnotationLayerProps {
  anchors: AnnotationAnchors;
  items: readonly Annotation[];
}

export function AnnotationLayer({ anchors, items }: AnnotationLayerProps) {
  const { t } = useLocale();
  const nodes = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    const place = (
      screen: ReadonlyArray<{ id: string; x: number; y: number; visible: boolean }>,
    ) => {
      const placed: Array<{ left: number; top: number; right: number; bottom: number }> = [];

      for (const anchor of screen) {
        const element = nodes.current.get(anchor.id);
        if (!element) continue;

        if (!anchor.visible) {
          element.style.visibility = 'hidden';
          continue;
        }

        const width = element.offsetWidth;
        const height = element.offsetHeight;
        const box = {
          left: anchor.x - width / 2,
          top: anchor.y - height / 2,
          right: anchor.x + width / 2,
          bottom: anchor.y + height / 2,
        };

        const overlaps = placed.some(
          (other) =>
            box.left < other.right &&
            box.right > other.left &&
            box.top < other.bottom &&
            box.bottom > other.top,
        );

        if (overlaps) {
          element.style.visibility = 'hidden';
          continue;
        }

        placed.push(box);
        element.style.visibility = 'visible';
        element.style.transform = `translate3d(${box.left.toFixed(1)}px, ${box.top.toFixed(1)}px, 0)`;
      }
    };

    const off = anchors.onUpdate(place);
    // Ask for the current positions rather than waiting for the camera to
    // move: this layer usually mounts just after a framing move has settled.
    anchors.refresh();
    return off;
  }, [anchors, items]);

  if (items.length === 0) return null;

  return (
    <div className="annotations" aria-hidden="false">
      {items.map((item) => (
        <div
          key={item.id}
          className="annotation"
          style={{ visibility: 'hidden' }}
          ref={(element) => {
            if (element) nodes.current.set(item.id, element);
            else nodes.current.delete(item.id);
          }}
        >
          <span>{item.labelAr}</span>
          {item.area !== null ? (
            <span className="annotation__area num">
              {item.area.toFixed(1)} {t('fact.squareMetres')}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}
